import { useMutation } from '@apollo/client';
import { InfoOutlined } from '@mui/icons-material';
import { Box } from '@mui/material';
import { FileType } from '@procureai/constellation/foundation/file';
import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import { FormSchemaProvider } from '@procureai/ui-form-mui';
import { Widget, WidgetHeader, WidgetIcon, WidgetEditActions, useUuid, useCustomSnackbar } from '@procureai/ui-mui';
import { useFileStorage } from '@procureai/uix';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';

import { useUserDetail } from '../Module.hooks';
import { I18nNamespace } from '~/common/types';
import { useUser } from '~/hooks/useUser/useUser';
import { USER_PROFILE_QUERY } from '~/hooks/useUser/useUser.query.gql';

import { USER_PROFILE_MUTATION } from './UserProfileDetailsWidgetMutation.gql';
import { UserProfileContent } from './UserProfileSummary/UserProfileProfileForm/UserProfileContent/UserProfileContent';
import { UserProfileForm } from './UserProfileSummary/UserProfileProfileForm/UserProfileForm';
import {
  userProfileFormDataDefault,
  userProfileFormSchema,
  UserProfileFormData,
} from './UserProfileSummary/UserProfileProfileForm/UserProfileForm.schema';
import { UserProfileSummary } from './UserProfileSummary/UserProfileSummary';

export interface UserProfileDetailsWidgetProps {
  // widget is part of a bigger form -> do not add own form and saving capabilities
  hasParentForm?: boolean;
}

export const UserProfileDetailsWidget = ({ hasParentForm }: UserProfileDetailsWidgetProps) => {
  const { userId } = useUserDetail();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'UserProfileDetailsWidget' });
  const [selectedPicture, setSelectedPicture] = useState<File>();
  const { user } = useAuth();
  const { upload: falconUpload } = useFileStorage();
  const { hasPermission } = usePermissions();

  const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();
  const [editOpen, setEditOpen] = useState(hasParentForm);
  const formId = useUuid();
  const isHimself = !userId || userId === user?.profile.user_id;
  const canEditHis = isHimself && hasPermission(Permission.UserProfileEdit);
  const canEditOther = hasPermission(Permission.UserProfileManage);
  const canEdit = canEditHis || canEditOther;

  const { profile: userProfile, loading } = useUser({ userId: (isHimself ? user?.profile.user_id : userId) as string });

  const [update, { loading: saving }] = useMutation(USER_PROFILE_MUTATION, {
    onError,
    refetchQueries: [{ query: USER_PROFILE_QUERY, variables: { userId } }],
  });

  const handleSubmit = async (formData: UserProfileFormData) => {
    let pictureId: string | undefined = undefined;
    if (selectedPicture) {
      const { error, data } = await falconUpload({
        data: { file: selectedPicture, variant: FileType.PROFILE_PICTURE },
      });
      if (error) {
        onError(t('upload.error'));
      } else {
        pictureId = data.uploadId;
      }
    }

    const { errors } = await update({
      variables: {
        userId,
        data: { ...formData, pictureId },
      },
    });

    if (!errors) {
      setEditOpen(false);
      enqueueSuccessSnackbar(t('updateSuccess'));
    } else {
      onError(errors);
    }
  };

  const handleEditToggle = () => {
    setSelectedPicture(undefined);
    setEditOpen((prev) => !prev);
  };

  let content = null;

  if (editOpen) {
    content = <UserProfileForm />;

    if (!hasParentForm) {
      content = (
        <FormSchemaProvider<UserProfileFormData>
          id={formId}
          data={userProfile}
          schema={userProfileFormSchema}
          onSubmit={handleSubmit}
          defaultValues={userProfileFormDataDefault}
        >
          {content}
        </FormSchemaProvider>
      );
    }
  } else if (!loading && userProfile) {
    content = <UserProfileContent data={userProfile} />;
  }

  return (
    <Widget data-intercom-target="UserProfileDetailsWidget">
      <WidgetHeader
        title={t('title')}
        icon={<WidgetIcon icon={<InfoOutlined fontSize="small" />} />}
        actionsRight={
          canEdit ? (
            <WidgetEditActions formId={formId} saving={saving} open={editOpen} onToggle={handleEditToggle} />
          ) : undefined
        }
      />
      <Box
        sx={{
          display: 'flex',
          gap: '20px',
          padding: 4,
          flexDirection: 'column',
        }}
      >
        <UserProfileSummary
          userProfile={userProfile}
          isEditOpen={canEditHis && editOpen}
          picture={selectedPicture}
          onPictureEdit={setSelectedPicture}
        />
        {content}
      </Box>
    </Widget>
  );
};
